import { Temporal } from 'temporal-polyfill';
import { clock } from '@/models/common/clock';
import {
  type DueDate,
  type Lead,
  LeadContactability,
  type LeadContactability as LeadContactabilityType,
  LeadLifecycleStatus,
  LeadUrgency,
  type Workflow,
} from '@/models/entities/lead';
import { LeadStatusGroups } from '@/models/ui/leadStatusGroup.ts';

export function urgencyFromDueDate(dueDate: DueDate): LeadUrgency | null {
  const nowPlain = clock.getPlainDateISO();

  if (Temporal.PlainDate.compare(dueDate, nowPlain) === -1) {
    return LeadUrgency.Overdue;
  }

  const diff = nowPlain.until(dueDate, { largestUnit: 'day' });
  if (diff.days >= 0 && diff.days <= 7) {
    return LeadUrgency.OverdueSoon;
  }

  return null;
}

export function urgencyFromLead(lead: Lead): LeadUrgency | null {
  if (lead.workflow.status !== LeadLifecycleStatus.Lead) {
    return null;
  }

  return urgencyFromDueDate(lead.maintenance.dueDate);
}

export function contactabilityFromLead(
  lead: Lead,
): LeadContactabilityType | null {
  if (lead.workflow.status !== LeadLifecycleStatus.Lead) {
    return null;
  }

  return lead.workflow.customerUnreachableAt
    ? LeadContactability.Unreachable
    : null;
}

export function extendWorkflow(
  workflow: Omit<
    Workflow,
    'daysUntilDue' | 'status' | 'urgency' | 'contactability' | 'statusGroup'
  >,
  dueDate: DueDate,
): Workflow {
  const getDaysUntilDue = (): number => {
    const nowPlain = clock.getPlainDateISO();
    return nowPlain.until(dueDate, { largestUnit: 'day' }).days;
  };

  const _calculateStatus = () => {
    if (workflow.archivedAt) return LeadLifecycleStatus.Archived;
    if (workflow.claimSubmittedAt) return LeadLifecycleStatus.Executed;
    if (workflow.workFinishedAt) return LeadLifecycleStatus.Executed;
    if (workflow.workStartedAt) return LeadLifecycleStatus.InRepair;
    if (workflow.scheduledDate) return LeadLifecycleStatus.Scheduled;
    return LeadLifecycleStatus.Lead;
  };

  const status = _calculateStatus();
  const urgency =
    status === LeadLifecycleStatus.Lead ? urgencyFromDueDate(dueDate) : null;
  const contactability =
    status === LeadLifecycleStatus.Lead && workflow.customerUnreachableAt
      ? LeadContactability.Unreachable
      : null;

  const statusGroup = LeadStatusGroups.from(status);

  return {
    ...workflow,
    daysUntilDue: getDaysUntilDue(),
    status,
    urgency,
    contactability,
    statusGroup,
  } as Workflow;
}
